<script lang="ts">
    import {
        VpArrowRightIcon,
        VpPlayIcon,
    } from '@decathlon-experience/icons/svelte';
    import { parseDate } from '@internationalized/date';
    import { createDatePicker } from '@melt-ui/svelte';
    import dayjs from 'dayjs';

    import { trans } from '$assets/shop-translator.js';
    import cn from '$common/utils/cn';
    import Button from '$saga/components/Button.svelte';
    import CalendarPicker from '$saga/components/CalendarPicker.svelte';
    import { TOUR_TYPE_FIT, TOUR_TYPE_GIR } from '$saga/constants/tour';
    import { locale as localeStore } from '$saga/store/locale';
    import {
        findFirstAvailableMonth,
        formatTourDate,
        formatTourDate2,
        isDateDisabled as isDateDisabledUtil,
    } from '$saga/utils/date';
    import { formatCurrency } from '$saga/utils/price';
    import {
        datesInfos,
        dateValue,
        selectedPrice,
    } from '$saga/views/Tour/store';
    import type { ClosedSales } from '$saga/views/Tour/types';
    import type {
        MinPriceDays,
        VisibleDate,
    } from '$saga/views/Tour/VisibleDates/types';
    import {
        getCurrentDayPriceAmount,
        getQuantileSizeClass,
    } from '$saga/views/Tour/VisibleDates/utils';

    export let dates: VisibleDate[],
        tourType: number,
        handleNextStep: (date: string, price?: number) => void,
        closedSales: ClosedSales,
        minPricePerDays: MinPriceDays,
        overflowContainer: HTMLElement | null,
        hideStock: boolean = false;

    const NUMBER_OF_MONTHS = 3;
    // bug in DatePicker: setMonth and setYear are not working,
    // we use placeholder to set the visible month instead
    const firstAvailableDate = findFirstAvailableMonth(closedSales);

    const {
        elements: { calendar, cell, grid, heading, nextButton, prevButton },
        states: { value, months, headingValue, weekdays },
        helpers: { isDateDisabled, isDateUnavailable },
    } = createDatePicker({
        locale: $localeStore,
        name: 'selectedDate',
        numberOfMonths: NUMBER_OF_MONTHS,
        pagedNavigation: true,
        minValue: parseDate(dayjs().add(1, 'day').format('YYYY-MM-DD')),
        isDateDisabled: (date) =>
            isDateDisabledUtil(date, closedSales, minPricePerDays),
        value: dateValue,
        defaultPlaceholder: parseDate(firstAvailableDate.format('YYYY-MM-DD')),
    });

    const handleGirDateSelection = (selectedItem: VisibleDate) => {
        if (selectedItem?.date1?.date != null) {
            const actualPrice = selectedItem.hasRebate
                ? selectedItem.rebatedPrice
                : selectedItem.price;
            $selectedPrice = {
                originalPrice: selectedItem.price,
                actualPrice: actualPrice,
                rebateAmount: selectedItem.rebateAmount
                    ? Math.round(selectedItem.rebateAmount / 100)
                    : null,
            };
            const date = dayjs(selectedItem.date1.date).format('YYYY-MM-DD');
            dateValue.set(parseDate(date));
            handleNextStep(
                dayjs(selectedItem.date1.date).format('YYYY-MM-DD'),
                actualPrice ?? undefined,
            );
            $datesInfos = {
                price: actualPrice,
                remainingStock: selectedItem?.remaining_stock ?? null,
                statusName: selectedItem?.statusName ?? '',
                selectedDate: date,
            };
        }
    };

    const handleFitDateSelection = () => {
        const date = dayjs($dateValue?.toString()).format('YYYY-MM-DD');
        const selected = minPricePerDays[date];
        if (selected) {
            const actualPrice = selected.amount / 100;
            $selectedPrice = {
                originalPrice: selected.originalAmount
                    ? selected.originalAmount / 100
                    : null,
                rebateAmount: selected.rebateAmount
                    ? selected.rebateAmount / 100
                    : null,
                actualPrice: actualPrice,
            };
            $datesInfos = {
                price: actualPrice,
                remainingStock: null,
                statusName: '',
                selectedDate: date,
            };
            handleNextStep(date, actualPrice);
        }
    };
</script>

{#if tourType === TOUR_TYPE_GIR}
    <div class="flex flex-col gap-s py-l">
        <h3 class="my-s text-center font-semibold">
            {trans('tour.dates.months_select')}
        </h3>
        {#each dates as date (date.id)}
            <button
                type="button"
                class="flex w-full rounded-lg border border-solid border-border-quiet p-s text-sm hover:border-border-brand hover:bg-extended-cobalt-quiet"
                disabled={date.isFull}
                on:click={() => !date.isFull && handleGirDateSelection(date)}
            >
                <span class="flex w-full flex-col gap-xs text-left">
                    <span class={cn(date.isFull && 'opacity-[0.38]')}>
                        {trans('tour.dates.from')}
                        <span class="font-semibold"
                            >{formatTourDate(date.date1.date)}</span
                        >
                        {trans('tour.dates.to')}
                        <span class="font-semibold"
                            >{formatTourDate2(date.date2.date)}</span
                        >
                    </span>
                    {#if !hideStock}
                        {#if date.isFull}
                            <span class="font-semibold text-content-brand">
                                {trans('tour.dates.statuses.4')}
                            </span>
                        {:else}
                            <span class="text-content-brand">
                                <span class="font-semibold"
                                    >{date.statusName}</span
                                >
                            </span>
                        {/if}
                        {#if date.remaining_stock > 0}
                            <span class="text-xs">
                                {trans('tour.dates.statuses.available', {
                                    '%count%': date.remaining_stock,
                                })}
                                {#if date.paxSoldCount > 0}
                                    <span
                                        >- {date.paxSoldCount}
                                        {trans('tour.dates.registered')}</span
                                    >
                                {/if}
                            </span>
                        {/if}
                    {/if}
                    <span
                        class={cn(
                            'flex items-center gap-xs',
                            date.isFull && 'opacity-[0.38]',
                        )}
                    >
                        {#if date.hasRebate && date.rebateAmount}
                            {#if date.rebatedPrice}
                                <span
                                    class="bg-status-negative p-2xs font-semibold text-white"
                                >
                                    {formatCurrency(date.rebatedPrice, false)}
                                </span>
                            {/if}
                            <span
                                class="text-xs text-content-quiet line-through"
                            >
                                {formatCurrency(date.price, false)}
                            </span>
                            <span class="text-xs text-status-negative">
                                -{formatCurrency(
                                    Math.round(date.rebateAmount / 100),
                                    false,
                                )}
                            </span>
                        {:else}
                            <span
                                class="bg-container-commercial-catchy p-2xs font-semibold"
                            >
                                {formatCurrency(date.price, false)}
                            </span>
                        {/if}
                    </span>
                </span>
                <span class="m-auto flex h-full items-center p-m">
                    <VpArrowRightIcon
                        class={cn(
                            'size-xl text-content-neutral',
                            date.isFull && 'text-content-quiet opacity-[0.38]',
                        )}
                    />
                </span>
            </button>
        {/each}
    </div>
{:else if tourType === TOUR_TYPE_FIT}
    <div class="flex flex-col gap-xl">
        <h3 class="pt-m text-center font-semibold">
            {trans('tour.dates.months_select')}
        </h3>
        <CalendarPicker
            {calendar}
            {prevButton}
            {nextButton}
            {heading}
            {headingValue}
            {months}
            {grid}
            {weekdays}
            {isDateDisabled}
            {isDateUnavailable}
            {cell}
            numberOfMonths={NUMBER_OF_MONTHS}
            monthsOrientation="vertical"
            getDayAdditionalStyle={(date) =>
                getQuantileSizeClass(
                    minPricePerDays[dayjs(date.toString()).format('YYYY-MM-DD')]
                        ?.quantile,
                )}
            getDayAdditionalContent={(date) =>
                getCurrentDayPriceAmount(minPricePerDays, date)}
            {overflowContainer}
            displayNextMonthsLabel={trans(
                'tour.dates.display_next_months_label',
            )}
            displayPreviousMonthsLabel={trans(
                'tour.dates.display_previous_months_label',
            )}
        />
        <!-- Footer -->
        <div
            class="sticky bottom-0 left-0 flex w-full justify-between border-t border-solid border-border-quiet bg-white py-s"
        >
            <Button
                class={cn(
                    'w-full',
                    $value == null && 'cursor-not-allowed opacity-[0.38]',
                )}
                disabled={$value == null}
                on:click={handleFitDateSelection}
                endIcon={VpPlayIcon}
            >
                {trans('common.continue')}
            </Button>
        </div>
    </div>
{/if}
